import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { getRoomStatus } from '../../api/spotify.api';
import { ROUTES } from '../../config/routes';
import { useSearch } from './useSearch';
import { useQueue } from './useQueue';
import { useSentTracks } from './useSentTracks';
import { SearchBar } from '../../shared/components/SearchBar';
import { SearchResults } from '../../shared/components/SearchResults';
import { SongList } from '../../shared/components/SongList';
import { NowPlaying, LiveDot } from '../../shared/components/NowPlaying';
import { Grain, Wordmark, LoadingScreen } from '../../shared/components/Lounge';

export function RoomView({ roomId, available: initialAvailable = 0, isStatic = false }) {
  const [available, setAvailable]   = useState(initialAvailable);
  const [statusReady, setStatusReady] = useState(false);
  const [mode, setMode]             = useState(null); // 'playlist' = solo canciones de la playlist VIP
  const { results, loading: searching, error: searchError, hasSearched, playlistName, search } = useSearch(roomId, mode);
  const { queue, sending, sendError, add, remove, reorder, send } = useQueue(roomId, setAvailable);
  const { sent, nowPlaying, ready: sentReady, refresh: refreshSent } = useSentTracks(roomId);
  const [sendingFlow, setSendingFlow] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const reduce   = useReducedMotion();
  const navigate = useNavigate();

  // Cuando todas las canciones enviadas ya sonaron, llevar a la pantalla de cierre.
  useEffect(() => {
    if (sent.length === 0 || !sent.every((s) => s.played) || available > 0) return;
    const id = setTimeout(() => {
      localStorage.removeItem(`queuebar:queue:${roomId}`);
      navigate(ROUTES.GRACIAS, { state: { sent }, replace: true });
    }, 2500);
    return () => clearTimeout(id);
  }, [sent, available, navigate, roomId]);

  // El available real vive en el backend; bloquea el render hasta que resuelva.
  useEffect(() => {
    let active = true;
    getRoomStatus(roomId)
      .then((data) => {
        if (!active) return;
        if (data?.available !== undefined) setAvailable(data.available);
        setMode(data?.mode ?? null);
      })
      .catch(() => {})
      .finally(() => { if (active) setStatusReady(true); });
    return () => { active = false; };
  }, [roomId]);

  if (!statusReady || !sentReady) return <LoadingScreen />;

  const handleSend = async () => {
    setPendingCount(queue.length);
    setSendingFlow(true);
    try {
      await send();
      await refreshSent();
      // Confirmación visible: "En cola" queda debajo de los resultados de búsqueda
      document.getElementById('en-cola')
        ?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    } finally {
      setSendingFlow(false);
    }
  };

  const stagedUris = new Set(queue.map((s) => s.uri));

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="relative min-h-dvh bg-lounge-base font-inter text-lounge-text antialiased
                 selection:bg-lounge-brass/30"
    >
      <Grain />

      {/* Barra superior */}
      <header className="sticky top-0 z-40 border-b border-lounge-line bg-lounge-base/75
                         pt-[env(safe-area-inset-top)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[480px] items-center justify-between px-5 lg:max-w-6xl lg:px-12">
          <div className="flex items-center gap-3 sm:gap-4">
            <Wordmark className="text-[13px]" />
            <span className="flex items-center gap-1.5">
              <LiveDot />
              <span className="hidden font-mono min-[360px]:inline text-[10px] uppercase tracking-[0.2em] text-felt">En vivo</span>
            </span>
          </div>

          <span
            aria-label={`${available} ${available === 1 ? 'crédito disponible' : 'créditos disponibles'}`}
            className="flex items-center gap-1.5 rounded-full border border-lounge-brass/30 bg-lounge-chassis
                       px-3.5 py-1.5"
          >
            <motion.span
              key={available}
              initial={reduce ? false : { scale: 1.35, opacity: 0.4 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="font-mono text-sm font-medium tabular-nums text-lounge-brass"
            >
              {available}
            </motion.span>
            <span className="font-mono text-[11px] tracking-wider text-lounge-stone">
              {available === 1 ? 'crédito' : 'créditos'}
            </span>
          </span>
        </div>
      </header>

      <motion.main
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className={`relative mx-auto grid max-w-[480px] gap-8 px-5 pt-6 lg:gap-12 lg:max-w-6xl lg:grid-cols-12
                    lg:gap-x-12 lg:px-12 lg:pb-16 lg:pt-10 ${queue.length > 0 ? 'pb-32' : 'pb-12'}`}
      >
        <div className="lg:sticky lg:top-28 lg:col-span-5 lg:self-start">
          <NowPlaying track={nowPlaying} />
        </div>

        <div className="flex min-w-0 flex-col gap-12 lg:col-span-7">
          {/* Móvil: buscar primero; desktop: al final */}
          <div className="order-first lg:order-last">
            {available === 0 ? (
              <div className="rounded-lg border border-lounge-line bg-lounge-chassis px-6 py-10 text-center">
                <p className="font-serif text-2xl text-lounge-text">Sin créditos</p>
                <p className="mx-auto mt-2 max-w-xs font-inter text-sm text-lounge-stone">
                  {isStatic
                    ? 'Vuelve a escanear el QR para seguir agregando canciones'
                    : 'Esta sala ya no acepta más canciones'}
                </p>
              </div>
            ) : (
              <section aria-label="Buscar canciones">
                <h2 className={`font-serif text-[22px] font-medium text-lounge-text ${mode === 'playlist' ? 'mb-1' : 'mb-4'}`}>
                  Elige tu canción
                </h2>
                {mode === 'playlist' && (
                  <p className="mb-4 font-inter text-sm text-lounge-stone">
                    {playlistName ? <>De la playlist <span className="text-lounge-brass">{playlistName}</span></> : 'De la playlist del local'}
                  </p>
                )}
                <SearchBar onSearch={search} loading={searching} />
                <div className="mt-3">
                  <SearchResults
                    results={results}
                    stagedUris={stagedUris}
                    canAdd={queue.length < available}
                    onAdd={add}
                    error={searchError}
                    loading={searching}
                    hasSearched={hasSearched}
                  />
                </div>
              </section>
            )}
          </div>

          <SongList
            queue={queue}
            sent={sent}
            sending={sending}
            sendingFlow={sendingFlow}
            pendingCount={pendingCount}
            sendError={sendError}
            onReorder={reorder}
            onRemove={remove}
            onSend={handleSend}
          />
        </div>
      </motion.main>
    </motion.div>
  );
}
